interface RequestOptions extends RequestInit {
  timeout?: number;
  retries?: number;
}

export class RequestError extends Error {
  statusCode: number;
  details?: any;

  constructor(message: string, statusCode: number, details?: any) {
    super(message);
    this.name = 'RequestError';
    this.statusCode = statusCode;
    this.details = details;
  }
}

function combineAbortSignals(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();
  
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort();
      break;
    }
    signal.addEventListener('abort', () => controller.abort());
  }
  
  return controller.signal;
}

export async function request<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const {
    timeout = 60000, // 60 seconds default
    retries = 3,
    signal,
    ...fetchOptions
  } = options;

  // Create timeout controller
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeout);

  // Combine abort signals if one was provided
  const combinedSignal = signal
    ? combineAbortSignals([signal, timeoutController.signal])
    : timeoutController.signal;

  try {
    let lastError: Error | null = null;

    // Detect FormData to avoid setting JSON Content-Type
    const isFormData = fetchOptions.body instanceof FormData;

    const mergedHeaders = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...fetchOptions.headers,
    };

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const response = await fetch(url, {
          ...fetchOptions,
          headers: mergedHeaders,
          signal: combinedSignal,
        });

        clearTimeout(timeoutId);

        // Handle non-2xx responses
        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          let errorDetails: any = null;

          try {
            const contentType = response.headers.get('content-type');
            if (contentType?.includes('application/json')) {
              const errorBody = await response.json();
              errorMessage = errorBody.message || errorMessage;
              errorDetails = errorBody;
            } else {
              errorMessage = await response.text() || errorMessage;
            }
          } catch {
            // If we can't parse the error body, use the default message
          }

          throw new RequestError(errorMessage, response.status, errorDetails);
        }

        // Parse successful response
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          return await response.json();
        } else {
          return (await response.text()) as unknown as T;
        }
      } catch (error) {
        lastError = error as Error;

        // Don't retry on certain errors
        if (error instanceof RequestError && error.statusCode < 500) {
          throw error;
        }

        if (error instanceof Error && error.name === 'AbortError') {
          throw new RequestError('Request timed out', 408);
        }

        // Only retry on 5xx errors or network errors, and if we have attempts left
        if (attempt === retries - 1) {
          throw lastError;
        }

        // Wait before retrying (exponential backoff)
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError || new Error('Request failed after all retries');
  } finally {
    clearTimeout(timeoutId);
  }
} 